import {
  useCreateFormSubmission,
  useGetFormBySlug,
  useGetFormFields,
} from '@sunrise-ui/api-client';
import type { FieldResponseInput } from '@sunrise-ui/api/insights';
import {
  DynamicForm,
  withAuthGate,
  type FormValues,
} from '@sunrise-ui/composites';
import {
  Colors,
  Loader,
  TextBold,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

export function FormSubmissionScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { currentUserId } = useAuthStore();
  const styles = useDynamicStyles(createStyles);

  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fetch form data
  const { data: form, isLoading: isLoadingForm } = useGetFormBySlug(slug);
  const { data: fields = [], isLoading: isLoadingFields } = useGetFormFields(
    form?.id,
  );
  const { mutate: submitForm, isPending: isSubmitting } =
    useCreateFormSubmission();

  const isLoading = isLoadingForm || isLoadingFields;

  // Handle form submission
  const handleSubmit = async (values: FormValues) => {
    if (!form?.id) return;

    // Convert form values to field responses
    const fieldResponses: FieldResponseInput[] = Object.entries(values).map(
      ([fieldId, value]) => ({
        field_id: fieldId,
        value: value,
      }),
    );

    submitForm(
      {
        createFormSubmissionInput: {
          form_id: form.id,
          user_id: currentUserId,
          field_responses: fieldResponses,
        },
      },
      {
        onSuccess: () => {
          setIsSubmitted(true);
        },
      },
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <Loader />
      </View>
    );
  }

  // Form not found
  if (!form) {
    return (
      <View style={styles.errorContainer}>
        <TextBold style={styles.errorTitle}>Form Not Found</TextBold>
        <TextReg style={styles.errorText}>
          This form doesn&apos;t exist or is no longer available.
        </TextReg>
      </View>
    );
  }

  if (form.form_type === 'checkout') {
    return (
      <View style={styles.errorContainer}>
        <TextBold style={styles.errorTitle}>Form Not Available</TextBold>
        <TextReg style={styles.errorText}>
          This form is only available during event checkout.
        </TextReg>
      </View>
    );
  }

  // Form not published
  if (!form.is_published) {
    return (
      <View style={styles.errorContainer}>
        <TextBold style={styles.errorTitle}>Form Not Available</TextBold>
        <TextReg style={styles.errorText}>
          This form is not currently accepting responses.
        </TextReg>
      </View>
    );
  }

  // Success state
  if (isSubmitted) {
    const successMessage = form.success_message?.trim();

    return (
      <View style={styles.successContainer}>
        <TextBold style={styles.successTitle}>
          {successMessage && successMessage.length > 0
            ? successMessage
            : 'Your response has been submitted successfully.'}
        </TextBold>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      <View style={styles.header}>
        <TextBold style={styles.title}>{form.title}</TextBold>
        {form.description && (
          <TextReg style={styles.description}>{form.description}</TextReg>
        )}
      </View>

      {fields.length === 0 ? (
        <View style={styles.emptyContainer}>
          <TextReg style={styles.emptyText}>
            This form has no fields yet.
          </TextReg>
        </View>
      ) : (
        <DynamicForm
          fields={fields}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitButtonText={form.submit_button_text ?? 'Submit'}
          style={styles.form}
        />
      )}
    </ScrollView>
  );
}

export default withAuthGate(FormSubmissionScreen);

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.purple0,
    },
    contentContainer: {
      padding: isMobile ? 16 : 24,
      maxWidth: 600,
      alignSelf: 'center',
      width: '100%',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: Colors.purple0,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
      backgroundColor: Colors.purple0,
    },
    errorTitle: {
      fontSize: 20,
      color: Colors.text,
      marginBottom: 8,
    },
    errorText: {
      fontSize: 16,
      color: Colors.opacity50,
      textAlign: 'center',
    },
    successContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
      backgroundColor: Colors.purple0,
    },
    successTitle: {
      fontSize: 24,
      color: Colors.text,
      marginBottom: 12,
    },
    successText: {
      fontSize: 16,
      color: Colors.opacity50,
      textAlign: 'center',
    },
    header: {
      marginBottom: 24,
      gap: 8,
    },
    title: {
      fontSize: isMobile ? 24 : 28,
      color: Colors.text,
    },
    description: {
      fontSize: 16,
      color: Colors.opacity50,
    },
    emptyContainer: {
      padding: 24,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: 16,
      color: Colors.opacity50,
    },
    form: {
      gap: 20,
    },
  });
